import { get, post, del, put, upload } from '@api/request';
import { defaultPageSize } from '@constants';

export async function fetchSearchOptions(
  { appraisalService },
  authenticationToken,
) {
  const url = `${appraisalService}/appraisal/config/agentsearch`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function searchAgent(
  { appraisalService },
  authenticationToken,
  {
    pageSize = defaultPageSize,
    page = 1,
    sortField,
    sortDirection,
    filterModel,
    searchOptions,
  },
) {
  let url = `${appraisalService}/appraisal/agent/search`;
  url += `?page=${page}&pageSize=${pageSize}`;
  if (sortField && sortDirection) {
    url += `&sortField=${sortField}&sortDirection=${sortDirection}`;
  }
  if (filterModel && Object.keys(filterModel).length >= 0) {
    Object.keys(filterModel).forEach(filter => {
      url += `&searchField=${filter}&searchText=${filterModel[filter].value}`;
    });
  }
  const { body } = await post(authenticationToken, url, searchOptions);
  return body;
}

export async function fetchFields({ appraisalService }, authenticationToken) {
  const url = `${appraisalService}/appraisal/config/agentsearchresults`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchTags({ appraisalService }, authenticationToken) {
  const url = `${appraisalService}/appraisal/agent/tags`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchAgent(
  { appraisalService },
  authenticationToken,
  { agentId },
) {
  const url = `${appraisalService}/appraisal/agent/${agentId}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchAgentNotes(
  { appraisalService },
  authenticationToken,
  { agentId },
) {
  const url = `${appraisalService}/appraisal/agent/${agentId}/notes`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchAgentProperties(
  { appraisalService },
  authenticationToken,
  { agentId },
) {
  const url = `${appraisalService}/appraisal/agent/${agentId}/property`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function deleteAgentNote(
  { appraisalService },
  authenticationToken,
  { noteId },
) {
  const url = `${appraisalService}/appraisal/agentnote/${noteId}`;
  const { body } = await del(authenticationToken, url);
  return body;
}

export async function updateAgentNote(
  { appraisalService },
  authenticationToken,
  { noteId, data },
) {
  const url = `${appraisalService}/appraisal/agentnote/${noteId}`;
  const { body } = await put(authenticationToken, url, data);
  return body;
}

export async function addAgentNote(
  { appraisalService },
  authenticationToken,
  { id, data },
) {
  const url = `${appraisalService}/appraisal/agent/${id}/notes`;
  const { body } = await post(authenticationToken, url, data);
  return body;
}

export async function validateAgent(
  { appraisalService },
  authenticationToken,
  { agentId, data },
) {
  const url = `${appraisalService}/appraisal/agent/${agentId}/casscertification`;
  const { body } = await put(authenticationToken, url, data);
  return body;
}

export async function updateAgent(
  { appraisalService },
  authenticationToken,
  { agentId, data },
) {
  const url = `${appraisalService}/appraisal/agent/${agentId}`;
  const { body } = await put(authenticationToken, url, data);
  return body;
}

export async function deleteAgent(
  { appraisalService },
  authenticationToken,
  { agentId },
) {
  const url = `${appraisalService}/appraisal/agent/${agentId}`;
  const { body } = await del(authenticationToken, url);
  return body;
}

export async function createAgent(
  { appraisalService },
  authenticationToken,
  { data },
) {
  const url = `${appraisalService}/appraisal/agents`;
  const { body } = await post(authenticationToken, url, data);
  return body;
}

export async function updateAgentTag(
  { appraisalService },
  authenticationToken,
  { agentId, data },
) {
  const url = `${appraisalService}/appraisal/agent/${agentId}/tags`;
  const { body } = await put(authenticationToken, url, data);
  return body;
}

export async function fetchAgentEvents(
  { eventService },
  authenticationToken,
  { agentId },
) {
  const url = `${eventService}/events/search`;
  const { body } = await post(authenticationToken, url, {
    objectID: `${agentId}`,
    dataObject: 'agent',
  });
  return body;
}

export async function fetchAgentPropertiesByName(
  { appraisalService },
  authenticationToken,
  { agentId, page, pageSize, searchText, inactive },
) {
  let url = `${appraisalService}/appraisal/agent/${agentId}/property?page=${page}&pageSize=${pageSize}&searchText=${searchText}`;
  if (inactive !== undefined) {
    url = `${url}&inactive=${inactive}`;
  }
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchPropertyAgent(
  { appraisalService },
  authenticationToken,
  { pId, pYear, pRollCorr, pVersion },
) {
  const url = `${appraisalService}/appraisal/property/${pId}/${pYear}/${pVersion}/${pRollCorr}/agents`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function deletePropertyAgent(
  { appraisalService },
  authenticationToken,
  { propertyAccountAgentID },
) {
  const url = `${appraisalService}/appraisal/propertyaccountagent/${propertyAccountAgentID}`;
  const { body } = await del(authenticationToken, url);
  return body;
}

export async function fetchPropertyAccountAgent(
  { appraisalService },
  authenticationToken,
  { propertyAccountAgentID },
) {
  const url = `${appraisalService}/appraisal/propertyaccountagent/${propertyAccountAgentID}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function updatePropertyAccountAgent(
  { appraisalService },
  authenticationToken,
  { propertyAccountAgentID, data },
) {
  const url = `${appraisalService}/appraisal/propertyaccountagent/${propertyAccountAgentID}`;
  const { body } = await put(authenticationToken, url, data);
  return body;
}

export const insertPropertyAccountAgentFile = async (
  { appraisalService },
  authenticationToken,
  { propertyAccountAgentID, payload },
) => {
  const url = `${appraisalService}/appraisal/propertyaccountagent/${propertyAccountAgentID}/files`;
  const { body } = await upload(authenticationToken, url, payload);
  return body;
};

export const fetchPropertyAccountAgentFiles = async (
  { appraisalService },
  authenticationToken,
  { propertyAccountAgentID },
) => {
  const url = `${appraisalService}/appraisal/propertyaccountagent/${propertyAccountAgentID}/files`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const deletePropertyAccountAgentFile = async (
  { appraisalService },
  authenticationToken,
  { propertyAccountAgentID, s3ID },
) => {
  const url = `${appraisalService}/appraisal/propertyaccountagent/${propertyAccountAgentID}/file/${s3ID}`;
  const { body } = await put(authenticationToken, url);
  return body;
};

export const downloadPropertyAccountAgentFile = async (
  { appraisalService },
  authenticationToken,
  { propertyAccountAgentID, s3ID },
) => {
  const url = `${appraisalService}/appraisal/propertyaccountagent/${propertyAccountAgentID}/file/${s3ID}`;

  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const headers = new Headers();
  headers.append('Authorization', authenticationToken);

  return await fetch(url, { headers })
    .then(response => response.blob())
    .then(blobby => {
      const objectUrl = window.URL.createObjectURL(blobby);

      anchor.href = objectUrl;
      anchor.download = s3ID;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
};

export const updatePropertyAccountAgentFile = async (
  { appraisalService },
  authenticationToken,
  { propertyAccountAgentID, payload },
) => {
  const url = `${appraisalService}/appraisal/propertyaccountagent/${propertyAccountAgentID}/files`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const fetchAgentsByName = async (
  { appraisalService },
  authenticationToken,
  { pageSize, page, payload },
) => {
  const url = `${appraisalService}/appraisal/agent/search?sortField=addrStreet&sortDirection=ASC&pageSize=${pageSize}&page=${page}`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
};

export const fetchPropertyAccountNameListing = async (
  { appraisalService },
  authenticationToken,
  { pId, pYear, pVersion = 0, pRollCorr = 0 },
) => {
  const url = `${appraisalService}/appraisal/property/${pId}/${pYear}/${pVersion}/${pRollCorr}/ownernamelisting`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const insertPropertyAccountAgent = async (
  { appraisalService },
  authenticationToken,
  { propertyAccountID, payload },
) => {
  const url = `${appraisalService}/appraisal/propertyaccount/${propertyAccountID}/agents`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
};

export const insertPropertyListAccountAgent = async (
  { appraisalService },
  authenticationToken,
  { propertyList, fileList, payload },
) => {
  let body = null;
  let formData = null;
  let fileData = null;
  for (let x = 0; x < propertyList.length; x++) {
    body = await insertPropertyAccountAgent(
      { appraisalService },
      authenticationToken,
      {
        propertyAccountID: propertyList[x].pAccountID,
        payload,
      },
    );
    if (body && body.results && body.results[0]) {
      for (let x = 0; x < fileList.length; x++) {
        if (fileList[x]) {
          formData = new FormData();
          formData.append('file', fileList[x][0]);
          if (x === 0) {
            formData.append('lastUpdate', body.results[0].updateDt);
          } else {
            formData.append('lastUpdate', fileData.results[0].updateDt);
          }
          fileData = await insertPropertyAccountAgentFile(
            { appraisalService },
            authenticationToken,
            {
              propertyAccountAgentID: body.results[0].propertyAccountAgentID,
              payload: formData,
            },
          );
        }
      }
    }
    // if (body && body.results && body.results[0]) {
    //   agentData = await fetchPropertyAgent({ appraisalService }, authenticationToken, {
    //     pId: body.results[0].pID,
    //     pYear: body.results[0].pYear,
    //     pRollCorr: body.results[0].pRollCorr,
    //     pVersion: body.results[0].pVersion,
    //   });
    // }
  }
  return body;
};

export const fetchAgentEfile = async (
  { appraisalNoticeService },
  authenticationToken,
  { agentId },
) => {
  const url = `${appraisalNoticeService}/prodigyappraisalnotices/agent/${agentId}/efile`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const updateAgentEfile = async (
  { appraisalNoticeService },
  authenticationToken,
  { agentId, payload },
) => {
  const url = `${appraisalNoticeService}/prodigyappraisalnotices/agent/${agentId}/efile`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const agentEfileGeneratePin = async (
  { appraisalNoticeService },
  authenticationToken,
  { agentId, payload },
) => {
  const url = `${appraisalNoticeService}/prodigyappraisalnotices/agent/${agentId}/generatepin`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};
